import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Eye, LockKeyhole, Palette, Sparkles } from 'lucide-react';
import {
  CARD_AURAS,
  CARD_COLOR_HEX,
  CARD_COLOR_KEYS,
  CARD_CUSTOMIZATION_FEATURES,
  CARD_CUSTOMIZATION_LEVELS,
  CARD_EMBLEMS,
  CARD_FINISHES,
  CARD_FRAMES,
  CARD_NICKNAME_MAX,
  CARD_PORTRAITS,
  CARD_SIGNATURE_MAX,
  GAME_CONFIG,
  MAX_CARD_LEVEL,
  canCustomizeCard,
  cardCustomizationChanges,
  cardFeatureAtLevel,
  type CardCustomization,
} from '@anidex/shared/economy';
import { cardCustomizationPatchSchema } from '@anidex/shared/validation';
import type { CardOwnershipDTO, CharacterDTO } from '@anidex/shared/types';
import { ProgressBar } from '../../components/Badges';
import { CardEmblem } from '../../components/CharacterCard';
import { FragmentCrystal } from '../../components/FragmentCrystal';
import { useToasts } from '../../components/Toasts';
import { upgradeUnlock } from '../../i18n/catalog';
import { errorMessage } from '../../services/api';
import { formatNumber } from '../../services/format';
import { useSetCardCustomization } from '../../services/queries';
import { useSession } from '../../services/queries';
import { PremiumImageUpload } from '../../components/PremiumImageUpload';

interface CardWorkshopProps {
  character: CharacterDTO;
  ownership: CardOwnershipDTO;
  customization: CardCustomization;
  previewLevel: number | null;
  onPreview: (level: number | null) => void;
  onChange: (customization: CardCustomization | null) => void;
  tab: 'evolution' | 'customization';
  onTabChange: (tab: 'evolution' | 'customization') => void;
}

function StyleChoices<T extends string>({
  label, requiredLevel, level, disabled, values, value, optionLabel, onChange, renderOption,
}: {
  label: string;
  requiredLevel: number;
  level: number;
  disabled: boolean;
  values: readonly T[];
  value: T | null | undefined;
  optionLabel: (value: T) => string;
  onChange: (value: T | null) => void;
  renderOption?: (value: T) => ReactNode;
}) {
  const { t } = useTranslation('collection');
  return (
    <fieldset className="card-style-field" disabled={disabled}>
      <legend>
        {label}
        {level < requiredLevel && <span className="card-style-field__lock"><LockKeyhole size={12} />{t('workshop.atLevel', { level: requiredLevel })}</span>}
      </legend>
      <div className="card-style-choices">
        <button type="button" className="card-style-choice card-style-choice--auto" aria-pressed={!value} onClick={() => onChange(null)}>
          {t('workshop.automatic')}
        </button>
        {values.map((option) => (
          <button type="button" key={option} className="card-style-choice" aria-pressed={value === option} onClick={() => onChange(option)}>
            {renderOption?.(option)}
            <span>{optionLabel(option)}</span>
            {value === option && <Check className="card-style-choice__check" size={13} aria-hidden="true" />}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function CustomizationEditor({
  character, ownership, customization, onChange,
}: Pick<CardWorkshopProps, 'character' | 'ownership' | 'customization' | 'onChange'>) {
  const { t } = useTranslation('collection');
  const toasts = useToasts();
  const update = useSetCardCustomization();
  const premium = useSession().data?.user?.premium === true;
  const id = useId();
  const normalized = {
    ...customization,
    nickname: customization.nickname?.trim() || null,
    signature: customization.signature?.trim() || null,
  };
  const changes = cardCustomizationChanges(ownership.customization, normalized);
  const unchanged = Object.keys(changes).length === 0;
  const unavailable = (feature: keyof CardCustomization) =>
    !canCustomizeCard(ownership.upgradeLevel, feature) || Boolean(ownership.lock) || update.isPending;
  const swatch = (color: (typeof CARD_COLOR_KEYS)[number]) =>
    <span className="card-style-swatch" style={{ '--swatch': CARD_COLOR_HEX[color] } as CSSProperties} aria-hidden="true" />;

  return (
    <form
      className="card-customization"
      onSubmit={(event) => {
        event.preventDefault();
        const parsed = cardCustomizationPatchSchema.safeParse(changes);
        if (!parsed.success) {
          toasts.push({ tone: 'error', title: t('workshop.saveFailed'), description: t('workshop.invalidText') });
          return;
        }
        update.mutate({ characterId: character.id, customization: parsed.data }, {
          onSuccess: () => {
            onChange(null);
            toasts.push({ tone: 'success', title: t('workshop.saved') });
          },
          onError: (error) =>
            toasts.push({ tone: 'error', title: t('workshop.saveFailed'), description: errorMessage(error) }),
        });
      }}
    >
      <p className="hint">{t('workshop.customizeHint')}</p>
      <PremiumImageUpload kind="card" active={premium} characterId={character.id} imageUrl={ownership.customImageUrl ?? character.customImageUrl} disabled={Boolean(ownership.lock)} />
      {ownership.lock && <p className="card-workshop__notice" role="status"><LockKeyhole size={16} />{t('workshop.lockedCard')}</p>}
      <fieldset className="card-style-field" disabled={unavailable('nickname')}>
        <legend>
          {t('workshop.features.nickname')}
          {!canCustomizeCard(ownership.upgradeLevel, 'nickname') && <span className="card-style-field__lock"><LockKeyhole size={12} />{t('workshop.atLevel', { level: CARD_CUSTOMIZATION_LEVELS.nickname })}</span>}
        </legend>
        <label className="visually-hidden" htmlFor={`${id}-nickname`}>{t('workshop.features.nickname')}</label>
        <input
          id={`${id}-nickname`}
          className="input"
          value={customization.nickname ?? ''}
          onChange={(event) => onChange({ ...customization, nickname: event.target.value || null })}
          maxLength={CARD_NICKNAME_MAX}
          autoComplete="off"
          placeholder={character.name}
          aria-describedby={`${id}-nickname-hint`}
        />
        <p className="hint" id={`${id}-nickname-hint`}>{t('workshop.nicknameHint', { max: CARD_NICKNAME_MAX })}</p>
      </fieldset>
      <StyleChoices
        level={ownership.upgradeLevel}
        label={t('workshop.features.nameColor')} requiredLevel={CARD_CUSTOMIZATION_LEVELS.nameColor} disabled={unavailable('nameColor')}
        values={CARD_COLOR_KEYS} value={customization.nameColor} optionLabel={(color) => t(`workshop.colors.${color}`)}
        onChange={(nameColor) => onChange({ ...customization, nameColor })} renderOption={swatch}
      />
      <StyleChoices
        level={ownership.upgradeLevel}
        label={t('workshop.features.frame')} requiredLevel={CARD_CUSTOMIZATION_LEVELS.frame} disabled={unavailable('frame')}
        values={CARD_FRAMES} value={customization.frame} optionLabel={(frame) => t(`workshop.frames.${frame}`)}
        onChange={(frame) => onChange({ ...customization, frame })}
        renderOption={(frame) => <span className={`card-style-frame card-style-frame--${frame}`} aria-hidden="true" />}
      />
      <StyleChoices
        level={ownership.upgradeLevel}
        label={t('workshop.features.emblem')} requiredLevel={CARD_CUSTOMIZATION_LEVELS.emblem} disabled={unavailable('emblem')}
        values={CARD_EMBLEMS} value={customization.emblem} optionLabel={(emblem) => t(`workshop.emblems.${emblem}`)}
        onChange={(emblem) => onChange({ ...customization, emblem })} renderOption={(emblem) => <CardEmblem emblem={emblem} />}
      />
      <StyleChoices
        level={ownership.upgradeLevel}
        label={t('workshop.features.haloColor')} requiredLevel={CARD_CUSTOMIZATION_LEVELS.haloColor} disabled={unavailable('haloColor')}
        values={CARD_COLOR_KEYS} value={customization.haloColor} optionLabel={(color) => t(`workshop.colors.${color}`)}
        onChange={(haloColor) => onChange({ ...customization, haloColor })} renderOption={swatch}
      />
      <StyleChoices
        level={ownership.upgradeLevel}
        label={t('workshop.features.finish')} requiredLevel={CARD_CUSTOMIZATION_LEVELS.finish} disabled={unavailable('finish')}
        values={CARD_FINISHES} value={customization.finish} optionLabel={(finish) => t(`workshop.finishes.${finish}`)}
        onChange={(finish) => onChange({ ...customization, finish })}
        renderOption={(finish) => <span className={`card-style-finish card-style-finish--${finish}`} aria-hidden="true" />}
      />
      <fieldset className="card-style-field" disabled={unavailable('signature')}>
        <legend>
          {t('workshop.features.signature')}
          {!canCustomizeCard(ownership.upgradeLevel, 'signature') && <span className="card-style-field__lock"><LockKeyhole size={12} />{t('workshop.atLevel', { level: CARD_CUSTOMIZATION_LEVELS.signature })}</span>}
        </legend>
        <label className="visually-hidden" htmlFor={`${id}-signature`}>{t('workshop.features.signature')}</label>
        <input
          id={`${id}-signature`}
          className="input"
          value={customization.signature ?? ''}
          onChange={(event) => onChange({ ...customization, signature: event.target.value || null })}
          maxLength={CARD_SIGNATURE_MAX}
          autoComplete="off"
          placeholder={t('workshop.signaturePlaceholder')}
        />
        <p className="hint">{t('workshop.signatureHint', { max: CARD_SIGNATURE_MAX })}</p>
      </fieldset>
      <StyleChoices
        level={ownership.upgradeLevel}
        label={t('workshop.features.aura')} requiredLevel={CARD_CUSTOMIZATION_LEVELS.aura} disabled={unavailable('aura')}
        values={CARD_AURAS} value={customization.aura} optionLabel={(aura) => t(`workshop.auras.${aura}`)}
        onChange={(aura) => onChange({ ...customization, aura })}
      />
      <StyleChoices
        level={ownership.upgradeLevel}
        label={t('workshop.features.portrait')} requiredLevel={CARD_CUSTOMIZATION_LEVELS.portrait} disabled={unavailable('portrait')}
        values={CARD_PORTRAITS} value={customization.portrait} optionLabel={(portrait) => t(`workshop.portraits.${portrait}`)}
        onChange={(portrait) => onChange({ ...customization, portrait })}
      />
      <div className="card-customization__actions">
        <button type="button" className="button" disabled={unchanged || update.isPending} onClick={() => onChange(null)}>{t('workshop.cancel')}</button>
        <button type="submit" className="button button--primary" disabled={unchanged || Boolean(ownership.lock) || update.isPending}>
          {update.isPending ? t('workshop.saving') : t('workshop.save')}
        </button>
      </div>
      <p className="hint">{t('workshop.transferHint')}</p>
    </form>
  );
}

export function CardWorkshop({ character, ownership, customization, previewLevel, onPreview, onChange, tab, onTabChange }: CardWorkshopProps) {
  const { t } = useTranslation('collection');
  const id = useId();
  const rail = useRef<HTMLOListElement>(null);
  const selectedLevel = previewLevel ?? ownership.upgradeLevel;
  const selected = GAME_CONFIG.upgrades.find((entry) => entry.level === selectedLevel)!;
  const feature = cardFeatureAtLevel(selectedLevel);
  const reached = selectedLevel <= ownership.upgradeLevel;
  const step = ownership.nextUpgrade;
  const unlocked = CARD_CUSTOMIZATION_FEATURES.filter((entry) => canCustomizeCard(ownership.upgradeLevel, entry)).length;

  useEffect(() => {
    const container = rail.current;
    const active = container?.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
    if (container && active) {
      container.scrollLeft = active.offsetLeft - (container.clientWidth - active.clientWidth) / 2;
    }
  }, [selectedLevel, tab]);

  return (
    <section className="card-workshop" aria-labelledby={`${id}-title`}>
      <header className="card-workshop__header">
        <h3 id={`${id}-title`}>{t('workshop.title')}</h3>
        <span>{t('detail.levelOf', { level: ownership.upgradeLevel, max: MAX_CARD_LEVEL })}</span>
      </header>
      <div className="card-workshop__tabs" aria-label={t('workshop.title')}>
        <button type="button" aria-pressed={tab === 'evolution'} aria-controls={`${id}-panel`} onClick={() => onTabChange('evolution')}>
          <Sparkles size={17} aria-hidden="true" />{t('workshop.evolution')}
        </button>
        <button type="button" aria-pressed={tab === 'customization'} aria-controls={`${id}-panel`} onClick={() => { onTabChange('customization'); onPreview(null); }}>
          <Palette size={17} aria-hidden="true" />{t('workshop.customize')}<small>{unlocked}/9</small>
        </button>
      </div>
      {tab === 'evolution' ? (
        <div id={`${id}-panel`} className="card-levels">
          <p className="hint">{t('workshop.previewHint')}</p>
          <ol ref={rail} className="card-levels__rail" aria-label={t('workshop.levels')}>
            {GAME_CONFIG.upgrades.map((entry) => (
              <li key={entry.level}>
                <button
                  type="button"
                  aria-pressed={selectedLevel === entry.level}
                  aria-current={ownership.upgradeLevel === entry.level ? 'step' : undefined}
                  aria-label={`${t('card.level', { level: entry.level })}, ${upgradeUnlock(entry.level, entry.unlock)}, ${entry.level <= ownership.upgradeLevel ? t('workshop.reached') : t('workshop.notReached')}`}
                  className={entry.level <= ownership.upgradeLevel ? 'is-reached' : ''}
                  onClick={() => onPreview(entry.level === ownership.upgradeLevel ? null : entry.level)}
                >
                  <strong>{entry.level}</strong>
                  {entry.level <= ownership.upgradeLevel ? <Check size={11} aria-hidden="true" /> : <LockKeyhole size={11} aria-hidden="true" />}
                </button>
              </li>
            ))}
          </ol>
          <article className={`card-levels__milestone${reached ? ' is-reached' : ''}`}>
            <span className="card-levels__seal" aria-hidden="true">{selectedLevel === 10 ? <Sparkles size={23} /> : selectedLevel}</span>
            <div>
              <span className="card-levels__state">{reached ? t('workshop.reached') : selectedLevel === ownership.upgradeLevel + 1 ? t('workshop.next') : t('workshop.previewOnly')}</span>
              <h4>{upgradeUnlock(selected.level, selected.unlock)}</h4>
              <p>{t(`workshop.descriptions.${selectedLevel}`)}</p>
            </div>
            <p className="card-levels__fx"><Sparkles size={14} aria-hidden="true" />{t(`workshop.fx.${selectedLevel}`)}</p>
            {selectedLevel > 1 && <span className="card-levels__cost">{t('workshop.stepCost', { cost: formatNumber(selected.totalCost - GAME_CONFIG.upgrades[selectedLevel - 2]!.totalCost) })}</span>}
            {reached && feature && <button type="button" className="button button--small" onClick={() => { onTabChange('customization'); onPreview(null); }}>{t('workshop.useUnlock')}</button>}
          </article>
          {step ? (
            <div className="card-levels__next">
              <div className="card-levels__next-heading">
                <span>{t('workshop.nextUpgrade', { level: step.toLevel })}</span>
                <strong>{upgradeUnlock(step.toLevel, step.unlock)}</strong>
              </div>
              <div className="card-levels__fragments">
                <FragmentCrystal count={ownership.fragments} rarity={character.rarity} imageUrl={character.imageMediumUrl} size="sm" />
                <div>
                  <ProgressBar value={Math.min(ownership.fragments, step.cost)} max={step.cost} label={t('detail.fragmentMeter')} />
                  <span>{t('detail.fragmentCount', { count: step.cost, owned: formatNumber(ownership.fragments), cost: formatNumber(step.cost) })}</span>
                </div>
              </div>
              <p className="hint">
                {ownership.fragments >= step.cost
                  ? t('workshop.ready')
                  : t('workshop.remaining', { count: step.cost - ownership.fragments, formatted: formatNumber(step.cost - ownership.fragments) })}
              </p>
              <p className="hint">{t('workshop.duplicateYield', { count: GAME_CONFIG.rarities[character.rarity].fragmentsPerDuplicate })}</p>
            </div>
          ) : (
            <p className="card-workshop__complete"><Sparkles size={20} aria-hidden="true" />{t('detail.maxReached')}</p>
          )}
          <details className="card-levels__catalogue">
            <summary>{t('workshop.allUnlocks')}<ChevronDown size={17} aria-hidden="true" /></summary>
            <ol>
              {GAME_CONFIG.upgrades.map((entry) => (
                <li key={entry.level}>
                  <button type="button" aria-pressed={selectedLevel === entry.level} onClick={() => onPreview(entry.level === ownership.upgradeLevel ? null : entry.level)}>
                    <span className={`card-levels__number${entry.level <= ownership.upgradeLevel ? ' is-reached' : ''}`}>{entry.level}</span>
                    <span><strong>{upgradeUnlock(entry.level, entry.unlock)}</strong><small>{t(`workshop.fx.${entry.level}`)}</small></span>
                    {entry.level <= ownership.upgradeLevel ? <Check size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                  </button>
                </li>
              ))}
            </ol>
          </details>
        </div>
      ) : (
        <div id={`${id}-panel`}>
          <CustomizationEditor character={character} ownership={ownership} customization={customization} onChange={onChange} />
        </div>
      )}
    </section>
  );
}
